import React, {FC, RefObject, useRef, useState} from 'react';
import styled from 'styled-components';

import {useInitialEffect} from '../../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {AnnotationModel} from '../../../../../../libs/shared/core/src/models/api/annotationModel';
import {
  AnnotationAnnotatedResourcesEnum,
  AnnotationLinkedResourcesEnum,
} from '../../../../../../libs/shared/core/src/models/api/annotationTypes';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {NewPanelId, RViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {isMessageActivity} from '../../../../../models/api/activityModel';
import {CommentUnfurlErrorsEnum} from '../../../../../models/api/commentUnfurlModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {getFlatActivities} from '../../../../../store/data/dataSelectors';
import {expandActivityViewerAsyncAction} from '../../../../../store/panels/viewer/activity/activityViewerActions';
import {jumpToActivityAsyncAction} from '../../../../../store/panels/viewer/viewerPanelActions';
import {AnnotationSummaryContainer} from '../../../../common/annotation/annotationSummaryContainer';
import {ConversationPreviewContainer} from '../../../../common/conversations/conversationPreviewContainer';
import {ConversationCellContainer} from '../../../cells/conversationCellContainer';
import {JumpToActivity} from '../jumpToActivity';
import {CommentViewerBaseUnfurl, unfurlBorderRadius} from './commentViewerBaseUnfurl';

/*
 * Props.
 */

interface CommentViewerConversationUnfurlProps {
  panelId: NewPanelId;
  conversationId: number | undefined;
  annotation: AnnotationModel | undefined;
  unfurledConversation?: ConversationModel;
  unfurledActivityId?: number;
  unfurlError?: CommentUnfurlErrorsEnum;
  cellsContext: CellsContext;
  onUnfurlClick: () => void;
  isInteractionDisabled?: boolean;
}

/*
 * Styles.
 */

const StyledAnnotationSummaryButton = styled(ChromelessButton)`
  position: relative; /* For <JumpToActivity /> */
  min-width: 150px; /* For a no-wrapped <JumpToActivity /> */
  display: inline-block;
  padding: 4px 12px 2px 6px;
  margin: 9px 12px 1px 12px;
  border-radius: ${unfurlBorderRadius}px;
  background-color: ${(p) => p.theme.foreground};
  border: 1px solid ${(p) => p.theme.alphas.black20};
`;

const StyledFitContentDiv = styled.div`
  width: fit-content;
`;

/*
 * Component.
 */

export const CommentViewerConversationUnfurl: FC<CommentViewerConversationUnfurlProps> = (props) => {
  const {annotation} = props;
  const dispatch = useAppDispatch();
  const [shouldShowPreview, setShouldShowPreview] = useState(false);
  const conversationRef = useRef<HTMLDivElement>(null);
  const {panelId} = props;

  const isViewerPanelId = RViewerPanelId.is(panelId);

  const referencedActivityInThisConversationId = useAppSelector((state) => {
    if (annotation?.annotatedResourceType !== AnnotationAnnotatedResourcesEnum.MESSAGE) {
      return undefined;
    }

    const activities = getFlatActivities(state);
    const referencedActivityInThisConversation = activities.find(
      (a) =>
        a.conversationId === props.conversationId &&
        isMessageActivity(a) &&
        a.messageId === annotation.annotatedMessageId,
    );

    return referencedActivityInThisConversation?.id;
  });

  useInitialEffect(() => {
    trackInterfaceEvent(InterfaceEventsEnum.P3_CONVERSATION_PREVIEW_VIEW, {
      conversation_id: props.conversationId,
      unfurled_conversation_id: props.unfurledConversation?.id,
      unfurled_activity_id: props.unfurledActivityId,
      error: props.unfurlError,
    });
  });

  if (!props.unfurledConversation) {
    return null;
  }

  const onClick = () => {
    if (!RViewerPanelId.is(props.panelId)) {
      return;
    }
    if (annotation?.linkedResource.type !== AnnotationLinkedResourcesEnum.ACTIVITY) {
      return;
    }
    if (!referencedActivityInThisConversationId) {
      return;
    }

    dispatch(
      jumpToActivityAsyncAction(
        props.panelId,
        {id: referencedActivityInThisConversationId},
        props.unfurledActivityId,
      ),
    );

    dispatch(expandActivityViewerAsyncAction(props.panelId, referencedActivityInThisConversationId));
  };

  return (
    <>
      {annotation?.linkedResource.type === AnnotationLinkedResourcesEnum.ACTIVITY && (
        <StyledAnnotationSummaryButton onClick={onClick}>
          {referencedActivityInThisConversationId !== undefined && <JumpToActivity />}
          <AnnotationSummaryContainer
            annotation={annotation}
            referencedActivityId={annotation.linkedResource.linkedActivityId}
            shouldHideRecipient
          />
        </StyledAnnotationSummaryButton>
      )}
      <StyledFitContentDiv
        onMouseEnter={() => setShouldShowPreview(true)}
        onMouseLeave={() => {
          setShouldShowPreview(false);
        }}
      >
        <CommentViewerBaseUnfurl
          onUnfurlClick={props.onUnfurlClick}
          interactionId="commentViewerUnfurl"
          isInteractionDisabled={props.isInteractionDisabled}
        >
          {renderUnfurlContents(
            props.panelId,
            props.unfurledConversation,
            props.cellsContext,
            conversationRef,
            () => setShouldShowPreview(true),
            () => setShouldShowPreview(false),
          )}
          {shouldShowPreview && isViewerPanelId && (
            <ConversationPreviewContainer
              panelId={panelId}
              conversation={props.unfurledConversation}
              anchor={conversationRef}
              viewerConversationId={undefined}
              shouldRenderSummaryIfAvailable
            />
          )}
        </CommentViewerBaseUnfurl>
      </StyledFitContentDiv>
    </>
  );
};

/*
 * Helpers.
 */

function renderUnfurlContents(
  panelId: NewPanelId,
  conversation: ConversationModel,
  cellsContext: CellsContext,
  ref: RefObject<HTMLDivElement>,
  onMouseEnter: () => void,
  onMouseLeave: () => void,
) {
  return (
    <div ref={ref} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <ConversationCellContainer
        panelId={panelId}
        conversation={conversation}
        cellsContext={cellsContext}
        isExtraConversation={false}
        parameters={undefined}
        isPreview
      />
    </div>
  );
}
